import type React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import usertiming from 'app/shared/utils/performanceUtils';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type MapData from 'app/shared/models/MapData';
import type { IMap } from 'app/client/utils/map/gmapUtils';
import type Area from 'app/shared/models/Area';

// Actions
import AppActions from 'app/shared/flux/actions/AppActions';
import AreaActions from 'app/shared/flux/actions/AreaActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import UserSearchActions from 'app/shared/flux/actions/UserSearchActions';

// Components
import ListingsCount from 'app/shared/modules/map/ListingsCount';
import Spinner from 'app/shared/modules/Spinner';

// Utils / Misc
import constants from 'app/shared/constants/ConstantsBundle';
import controller from 'app/shared/wrappers/MapWrapper/controller';
import queryUtils from 'app/shared/utils/queryUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import { MapWrapper } from 'app/shared/wrappers/MapWrapper/styles';

// Map Components
import gmapUtils from 'app/client/utils/map/gmapUtils';
import GoogleMap from 'app/shared/modules/map/GoogleMap';
import MapBorder from 'app/shared/modules/map/MapBorder';
import MarkersContainer from 'app/shared/modules/map/MarkersContainer';
import MapLayerButtons from 'app/shared/modules/map/MapLayerButtons';
import MapRemoveBorderButton from 'app/shared/modules/map/MapRemoveBorderButton';
import NoResultsMapBanner from 'app/shared/modules/search-page/NoResultsMapBanner';
import isEmpty from 'lodash/isEmpty';
import { useMapState } from './hooks/useMapState';
import mapConstants from 'app/shared/constants/MapConstants';
import { Dispatch } from 'redux';

interface MapQuery {
  border?: boolean;
  lat?: number;
  lon?: number;
  zoom?: number;
  [key: string]: string | number | boolean | undefined;
}

interface MapDataWithBorder extends MapData {
  border?: boolean;
}

interface AreaResponse {
  resourceId?: string;
  [key: string]: unknown;
}

interface MapControllerProps {
  useStaticMap?: boolean;
  setIsMwebMapLoaded?: ((loaded: boolean) => void) | null;
}

interface NoResultsMapBannerProps {
  numDefaultFilterListings?: number;
  showBorder?: boolean;
  onZoomOut?: () => void;
  [key: string]: unknown;
}

const MapController: React.FC<MapControllerProps> = ({ useStaticMap = false, setIsMwebMapLoaded = null }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const area = useSelector((state: RootReduxState) => state.area.area);
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails.currentListing);
  const isInitialSsrPage = useSelector((state: RootReduxState) => state.app.isInitialSsrPage);
  const isMapPanning = useSelector((state: RootReduxState) => state.app.isMapPanning);
  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const listingsLoading = useSelector((state: RootReduxState) => !state.app.fetchListingsByCoordsComplete);
  const mapType = useSelector((state: RootReduxState) => state.app.mapType);
  const numDefaultFilterListings = useSelector((state: RootReduxState) => state.listings.numDefaultFilterListings);
  const previewListing = useSelector((state: RootReduxState) => state.listings.listingGroups.previewListing);
  const totalListings = useSelector((state: RootReduxState) => state.listings.totalListings);

  // Derived props
  const pathname = location.pathname;
  const query = useMemo(() => queryUtils.parse(location.search) as MapQuery, [location.search]);
  const isAreaUrl = useMemo(() => routeUtils.isAreaUrl(pathname), [pathname]);
  const isMapView = true;
  const isNearMeUrl = useMemo(() => routeUtils.isNearMeUrl(pathname), [pathname]);
  const isPadOrBuildingUrl = useMemo(() => routeUtils.isPadOrBuildingUrl(pathname), [pathname]);

  const { lat, lon, zoom, border, setBorder, updateMapData, setZoom } = useMapState(query.border);
  const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
  const [mapTypeState, setMapTypeState] = useState<string>(mapConstants.DEFAULT);
  const [tilesLoaded, setTilesLoaded] = useState<boolean>(false);

  const hasMapZoomedRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);
  const mapLoadedRef = useRef<boolean>(false);
  const shouldPanRef = useRef<boolean>(false);
  const shouldResetPageQueryRef = useRef<boolean>(false);
  const doubleClickedBoundaryRef = useRef<boolean>(false);
  const doubleClickedRef = useRef<boolean>(false);
  const handleIdleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const isProcessingBoundsChangeRef = useRef<boolean>(false);

  const resetPageQuery = useCallback(() => {
    shouldResetPageQueryRef.current = true;
  }, []);

  const removeBorder = useCallback(() => {
    if (!window.map) return;
    const mapData = gmapUtils.getMapData(window.map as IMap);
    if ('lat' in mapData) {
      const mapDataWithBorder = mapData as MapDataWithBorder;
      mapDataWithBorder.border = false;
      setBorder(false);
      dispatch(RouteActions.updateUrlWithMapData(mapDataWithBorder));
      dispatch(
        UserSearchActions.setCurrentSearch({
          mapData: mapDataWithBorder,
          waiting: undefined,
          resourceId: undefined,
        }),
      );
    }
  }, [dispatch, setBorder]);

  const shouldPanToListing = useCallback((e: MouseEvent | TouchEvent) => {
    // e.clientY handles mouse clicks.
    // e.touches handles mWeb touch events.
    const clientY =
      ((e as MouseEvent).clientY || ((e as TouchEvent).touches && (e as TouchEvent).touches[0].clientY)) -
      mapConstants.HEADER_OFFSET_PX;

    if (mapRef.current) {
      const mapHeight = mapRef.current.getBoundingClientRect().height;
      const panThreshold = mapHeight - mapConstants.PREVIEW_OFFSET_PX;

      if (clientY >= panThreshold) {
        shouldPanRef.current = true;
      } else {
        shouldPanRef.current = false;
      }
    }
  }, []);

  const setMap = useCallback((map: google.maps.Map) => {
    setGoogleMap(map);
  }, []);

  const setMapPanning = useCallback(
    (value: boolean) => {
      dispatch((dispatch: Dispatch<any>, getState: () => RootReduxState) => {
        const currentValue = getState().app.isMapPanning;
        // Only set if the value has changed
        if (currentValue !== value) {
          dispatch(AppActions.setAppStoreBool('isMapPanning', value));
        }
      });
    },
    [dispatch],
  );

  const handleIdle = useCallback(() => {
    if (handleIdleTimeoutRef.current) {
      clearTimeout(handleIdleTimeoutRef.current);
    }
    handleIdleTimeoutRef.current = window.setTimeout(() => {
      setMapPanning(false);
      // corresponds to debounceTimeMs in SplitMapTemplate
      // aka minimum time before fetchListings will be called
    }, mapConstants.BOUNDS_DEBOUNCE_MS + mapConstants.IDLE_TIMEOUT_DELAY_MS) as unknown as NodeJS.Timeout;
  }, [dispatch, setMapPanning]);

  const updateUrlToNewLocation = useCallback(
    (googleMapInstance: google.maps.Map) => {
      const currentMapData = { lat, lon, zoom };
      const shouldFetchNewArea = (isAreaUrl || isNearMeUrl) && border === false;

      if (!mountedRef.current) {
        return;
      }

      const mapData = gmapUtils.getMapData(googleMapInstance as unknown as IMap);
      if ('lat' in mapData) {
        const mapDataWithBorder = mapData as MapDataWithBorder;
        mapDataWithBorder.border = border ?? undefined;
      }

      let updatePromise: Promise<unknown> | null = null;

      if (shouldFetchNewArea) {
        const mapDataTyped = mapData as MapData;
        const getBestFitAreaAction = AreaActions.getBestFitArea({
          minLat: Number(mapDataTyped.minLat).toFixed(4),
          maxLat: Number(mapDataTyped.maxLat).toFixed(4),
          minLon: Number(mapDataTyped.minLon).toFixed(4),
          maxLon: Number(mapDataTyped.maxLon).toFixed(4),
        });
        const dispatchResult = dispatch(getBestFitAreaAction) as unknown as Promise<AreaResponse> | undefined;
        if (dispatchResult && typeof dispatchResult.then === 'function') {
          updatePromise = dispatchResult
            .then((bestFitArea: AreaResponse) => {
              if (bestFitArea && bestFitArea.resourceId) {
                const setAreaAction = AreaActions.setCurrentArea({ area: bestFitArea, status: undefined });
                return dispatch(setAreaAction);
              }
              return bestFitArea;
            })
            .then((areaResult: unknown) => {
              const hideBorderAfterApi = border === false;
              const mapDataAfterApi = gmapUtils.getMapData(googleMapInstance as unknown as IMap);

              if ('lat' in mapDataAfterApi) {
                const mapDataWithBorder = mapDataAfterApi as MapDataWithBorder;
                mapDataWithBorder.border = hideBorderAfterApi;
              }

              const mapDataTyped = mapDataAfterApi as MapData;
              const { lat: newLat, lon: newLon, zoom: newZoom } = mapDataTyped;

              updateMapData(newLat, newLon, newZoom);

              dispatch(
                RouteActions.updateUrlWithMapDataAndAreaResourceId({
                  areaResourceId: (areaResult as AreaResponse).resourceId,
                  mapData: mapDataAfterApi,
                }),
              );

              return areaResult;
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('Error fetching best fit area:', error);
            });
        }
      } else if (
        controller.latLonOrZoomChanged(gmapUtils.getMapData(googleMapInstance as unknown as IMap), currentMapData)
      ) {
        const mapDataTyped = mapData as MapData;
        const { lat: newLat, lon: newLon, zoom: newZoom } = mapDataTyped;

        updateMapData(newLat, newLon, newZoom);

        // Update URL with new map coordinates - this triggers listing fetch in SplitMapTemplate
        updatePromise = Promise.resolve(dispatch(RouteActions.updateUrlWithMapData(mapData)));
      }

      // Only reset the flag after all operations complete
      if (updatePromise) {
        updatePromise.finally(() => {
          isProcessingBoundsChangeRef.current = false;
        });
      } else {
        isProcessingBoundsChangeRef.current = false;
      }
    },
    [dispatch, lat, lon, zoom, border, isAreaUrl, isNearMeUrl, updateMapData],
  );

  const handleBoundsChangedDebounced = useCallback(
    (googleMapInstance: google.maps.Map) => {
      if (isProcessingBoundsChangeRef.current) {
        return;
      }

      isProcessingBoundsChangeRef.current = true;

      const mapData = gmapUtils.getMapData(googleMapInstance as unknown as IMap);

      dispatch(
        UserSearchActions.setCurrentSearch({
          mapData,
          waiting: undefined,
          resourceId: undefined,
        }),
      );

      // Removed fetchNumDefaultFilterListings call - count will be extracted from main listings response
      updateUrlToNewLocation(googleMapInstance);
    },
    [dispatch, updateUrlToNewLocation],
  );

  // Create a stable debounced version
  const debouncedBoundsChangedRef = useRef<ReturnType<typeof debounce> | null>(null);

  useEffect(() => {
    debouncedBoundsChangedRef.current = debounce(
      (googleMapInstance: google.maps.Map) => {
        handleBoundsChangedDebounced(googleMapInstance);
      },
      mapConstants.BOUNDS_DEBOUNCE_MS,
      {
        leading: false,
        trailing: true,
      },
    );

    return () => {
      debouncedBoundsChangedRef.current?.cancel();
    };
  }, [handleBoundsChangedDebounced]);

  const handleMapResizeDebounced = useMemo(
    () =>
      debounce(
        (googleMapInstance: google.maps.Map) => {
          window.google.maps.event.trigger(googleMapInstance, 'resize');
        },
        mapConstants.RESIZE_DEBOUNCE_MS,
        {
          leading: false,
          trailing: true,
        },
      ),
    [],
  );

  const handleBoundsChanged = useCallback(
    (googleMapInstance: google.maps.Map) => {
      // Prevents mWeb list from updating map coords in background when clicking on listing.
      if (!isMapView) {
        return;
      }

      if (mapLoadedRef.current && debouncedBoundsChangedRef.current) {
        debouncedBoundsChangedRef.current(googleMapInstance);
      }

      if (!isMapPanning && !isInitialSsrPage) {
        setMapPanning(true);
      }
    },
    [dispatch, isMapView, isMapPanning, isInitialSsrPage, setMapPanning],
  );

  const handleMapFirstIdle = useCallback(
    (googleMapInstance: google.maps.Map) => {
      mapLoadedRef.current = true;
      usertiming.mark('GoogleMap firstIdle');
      const mapData = gmapUtils.getMapData(googleMapInstance as unknown as IMap);
      dispatch(
        UserSearchActions.setCurrentSearch({
          mapData,
          isMapFirstIdle: true,
          waiting: undefined,
          resourceId: undefined,
        }),
      );
      dispatch(AppActions.setAppStoreBool('gmapLoaded', true));
    },
    [dispatch],
  );

  const handleMapTilesLoaded = useCallback(() => {
    setTilesLoaded(true);
    if (setIsMwebMapLoaded) {
      setIsMwebMapLoaded(true);
    }
  }, [setIsMwebMapLoaded]);

  const handleMapDrag = useCallback(() => {
    resetPageQuery();
  }, [dispatch, resetPageQuery]);

  const handleMapZoom = useCallback(
    (googleMapInstance: google.maps.Map) => {
      const currentZoom = googleMapInstance.getZoom();
      if (currentZoom !== undefined && currentZoom < constants.MIN_MAP_ZOOM) {
        googleMapInstance.setZoom(constants.MIN_MAP_ZOOM);
      }
      if ((googleMapInstance as unknown as IMap).zoom === zoom) {
        return;
      }
      resetPageQuery();
      hasMapZoomedRef.current = true;
    },
    [dispatch, zoom, resetPageQuery],
  );

  const handleZoomOut = useCallback(() => {
    if (zoom !== null) {
      setZoom(zoom - 1);
    }
  }, [zoom, setZoom]);

  const handleBoundarySingleClick = useCallback(
    (_googleMapInstance: google.maps.Map) => {
      if (!doubleClickedBoundaryRef.current) {
        removeBorder();
        dispatch(ListingEngineActions.clearActiveMarkerAndPreviewListing());

        if (isPadOrBuildingUrl) {
          dispatch(RouteActions.transitionToAreaWithMapData());
        }
      }
    },
    [dispatch, removeBorder, isPadOrBuildingUrl],
  );

  const handleBoundaryClick = useCallback(
    (googleMapInstance: google.maps.Map) => {
      resetPageQuery();
      doubleClickedBoundaryRef.current = false;
      window.setTimeout(() => handleBoundarySingleClick(googleMapInstance), mapConstants.CLICK_DELAY_MS);
    },
    [resetPageQuery, handleBoundarySingleClick],
  );

  const handleBoundaryDblClick = useCallback(() => {
    doubleClickedBoundaryRef.current = true;
  }, []);

  const handleMapSingleClick = useCallback(() => {
    if (!doubleClickedRef.current) {
      dispatch(ListingEngineActions.clearActiveMarkerAndPreviewListing());

      if (isPadOrBuildingUrl) {
        dispatch(RouteActions.transitionToAreaWithMapData());
      }
    }
  }, [dispatch, isPadOrBuildingUrl]);

  const handleMapClick = useCallback(() => {
    resetPageQuery();
    doubleClickedRef.current = false;
    window.setTimeout(handleMapSingleClick, mapConstants.CLICK_DELAY_MS);
  }, [resetPageQuery, handleMapSingleClick]);

  const handleMapDblClick = useCallback(() => {
    doubleClickedRef.current = true;
  }, []);

  const didMapTypeChange = useCallback((oldMapType: string, newMapType: string) => {
    return oldMapType !== newMapType;
  }, []);

  const handleMapTypeChange = useCallback(() => {
    if (!googleMap) return;

    if (googleMap.getMapTypeId() === 'hybrid') {
      googleMap.setMapTypeId(window.google.maps.MapTypeId.ROADMAP);
      setMapTypeState('roadmap');
    } else if (googleMap.getMapTypeId() === 'roadmap') {
      googleMap.setOptions({ styles: [] });
      googleMap.setMapTypeId(window.google.maps.MapTypeId.HYBRID);
      setMapTypeState('hybrid');
    }
  }, [googleMap]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      debouncedBoundsChangedRef.current?.cancel();

      if (handleIdleTimeoutRef.current) {
        clearTimeout(handleIdleTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.addEventListener('click', shouldPanToListing);
    window.addEventListener('touchstart', shouldPanToListing);

    setBorder(query.border ?? null);

    const newMapData = controller.getLatLonZoom({ area, currentListing, pathname, query });

    if (newMapData) {
      const { lat: newLat, lon: newLon, zoom: newZoom } = newMapData;
      updateMapData(newLat, newLon, newZoom);
    }

    return () => {
      window.removeEventListener('click', shouldPanToListing);
      window.removeEventListener('touchstart', shouldPanToListing);
    };
  }, [shouldPanToListing, updateMapData, area, currentListing, pathname, query, setBorder]);

  useEffect(() => {
    // Prevent map from moving when leaving area / listing page
    // (e.g, url updates to homepage but map hasn't unmounted yet)
    if (!isAreaUrl && !isPadOrBuildingUrl && !isNearMeUrl) {
      return;
    }

    const newMapData = controller.getLatLonZoom({ area, currentListing, pathname, query });
    if (newMapData) {
      const mapData = { lat, lon, zoom };
      if (controller.latLonOrZoomChanged(mapData, newMapData)) {
        updateMapData(newMapData.lat, newMapData.lon, newMapData.zoom);
      }
    }
  }, [
    area,
    currentListing,
    pathname,
    query,
    isAreaUrl,
    isPadOrBuildingUrl,
    isNearMeUrl,
    lat,
    lon,
    zoom,
    updateMapData,
  ]);

  useEffect(() => {
    if (didMapTypeChange(mapType, mapType)) {
      handleMapTypeChange();
    }

    handleIdle();

    // Handles showing border when clicking the back button
    // e.g., Going from area page -> Remove border -> Clicking back.
    if (query.border === false && border !== false) {
      setBorder(false);
    } else if (query.border !== false && border === false) {
      setBorder(true);
    }

    // Use setTimeout to force panTo logic to wait for next tick in event loop,
    // otherwise this logic misses detecting if prevProps and current props
    // have changed.
    setTimeout(() => {
      if (shouldPanRef.current && isMobile && previewListing && googleMap) {
        const previewCoords = {
          lat: previewListing.geo.lat,
          lng: previewListing.geo.lon,
        };
        googleMap.panTo(previewCoords); // Center listing in map view.
        shouldPanRef.current = false;
      }
    }, 0);
  }, [
    mapType,
    query.border,
    border,
    isMobile,
    previewListing,
    googleMap,
    handleMapTypeChange,
    handleIdle,
    didMapTypeChange,
    setBorder,
  ]);

  const showBorder = useMemo(
    () => border !== false && area.resourceId !== mapConstants.UNITED_STATES_RESOURCE_ID,
    [border, area.resourceId],
  );

  const showBanner = useMemo(
    () =>
      ((totalListings === 0 && numDefaultFilterListings > 0) || (totalListings === 0 && !showBorder)) &&
      isMobile &&
      !listingsLoading,
    [totalListings, numDefaultFilterListings, showBorder, isMobile, listingsLoading],
  );

  const isLoading = useMemo(() => !area.resourceId || listingsLoading, [area.resourceId, listingsLoading]);

  const ariaLabel = useMemo(
    () =>
      gmapUtils.getAccessibleLabel({
        isAreaUrl,
        isNearMeUrl,
        isPadOrBuildingUrl,
        area: area as Area,
        listing: currentListing,
      }),
    [isAreaUrl, isNearMeUrl, isPadOrBuildingUrl, area, currentListing],
  );

  const accessibleInstructions = useMemo(
    () => `Details about each listing, including address and price, can be found in the search results
        under the h2 heading 'Apartments for Rent'`,
    [],
  );

  const hideDynamicMap = useMemo(() => useStaticMap && !tilesLoaded, [useStaticMap, tilesLoaded]);

  if (isEmpty(area)) {
    return (
      <MapWrapper id="MapWrapper">
        <Spinner className="Spinner-wrapper" />
      </MapWrapper>
    );
  }

  if (!isMapView) {
    return null;
  }

  return (
    <MapWrapper ref={mapRef} id="MapWrapper">
      {/* @ts-expect-error GoogleMap is a connected component but loadable wrapper expects all props */}
      <GoogleMap
        onBoundsChanged={handleBoundsChanged}
        onMapClick={handleMapClick}
        onMapDblClick={handleMapDblClick}
        onMapDrag={handleMapDrag}
        onMapFirstIdle={handleMapFirstIdle}
        onMapResize={handleMapResizeDebounced}
        onMapZoom={handleMapZoom}
        onMapIdle={handleIdle}
        onTilesLoaded={handleMapTilesLoaded}
        lat={Number(lat)}
        lon={Number(lon)}
        setMap={setMap}
        zoom={Number(zoom)}
        hidden={hideDynamicMap}
        ariaLabel={ariaLabel}
        accessibleInstructions={accessibleInstructions}
      >
        {showBanner ? (
          <NoResultsMapBanner
            {...({
              numDefaultFilterListings,
              showBorder,
              onZoomOut: handleZoomOut,
            } as NoResultsMapBannerProps)}
          />
        ) : (
          <Fragment>
            <ListingsCount isLoading={isLoading} />
            {showBorder && <MapRemoveBorderButton mapType={mapTypeState} removeBorder={removeBorder} />}
          </Fragment>
        )}
        <MapLayerButtons />
        {showBorder && (
          <MapBorder
            {...({
              area,
              mapType: mapTypeState,
              onBoundaryClick: handleBoundaryClick,
              onBoundaryDblClick: handleBoundaryDblClick,
            } as unknown as Record<string, unknown>)}
          />
        )}
        <MarkersContainer zoom={Number(zoom)} />
      </GoogleMap>
    </MapWrapper>
  );
};

export default MapController;
